import {
    mdiAccountCashOutline,
    mdiAccountTie,
    mdiArrowDown,
    mdiArrowUp,
    mdiChevronDown,
    mdiChevronUp,
    mdiCodeBraces,
    mdiDelete,
    mdiDragVertical,
    mdiGavel,
    mdiMinusCircleOutline,
    mdiPlus,
} from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React from 'react';
import {
    AutomationWorker,
    RuleActionType,
} from '../../../services/worker-configuration/automation/worker-automation.types';
import { RULE_FIELD_HELPERS } from '../../../services/worker-configuration/automation/worker-automation.constants';
import {
    getActionMeta,
    RULE_NAME_MAX_LENGTH,
} from '../../../services/worker-configuration/automation/worker-automation.utils';
import { WorkerStore } from '../../../stores/worker-configuration/worker.store';
import {
    ConfigurationFieldError,
    ConfigurationFieldLabel,
} from './configuration-action-summary';
import { ConfigurationRuleActionPanel } from './configuration-rule-action-panel';
import { ConfigurationRuleMatchBlock } from './configuration-rule-match-block';
import * as S from './configuration-rule.styles';
import { RuleTableRow } from './configuration-rules-table.utils';

interface IConfigurationRuleCardProps {
    /** False while the list is searched, filtered or grouped — the visible order is not the priority. */
    canReorder: boolean;
    columns: string;
    disabled?: boolean;
    row: RuleTableRow;
    showCodeColumn: boolean;
    store: WorkerStore;
    worker: AutomationWorker;
}

function getActionBadgeVariant(type: RuleActionType): 'attention' | 'neutral' | 'primary' | 'success' {
    switch (type) {
        case 'SWITCH_TO_PRIVATE_PAY':
            return 'attention';
        case 'CREATE_APPEAL':
        case 'EXECUTE_ENTER_FUNCTION':
            return 'primary';
        case 'CREATE_MESSAGE':
            return 'success';
        default:
            return 'neutral';
    }
}

function getActionIcon(type: RuleActionType): string {
    switch (type) {
        case 'SWITCH_TO_PRIVATE_PAY':
            return mdiAccountCashOutline;
        case 'CREATE_APPEAL':
            return mdiGavel;
        case 'CREATE_MESSAGE':
            return mdiAccountTie;
        case 'EXECUTE_ENTER_FUNCTION':
            return mdiCodeBraces;
        default:
            return mdiMinusCircleOutline;
    }
}

export const ConfigurationRuleCard: React.FC<IConfigurationRuleCardProps> = observer(
    ({ canReorder, columns, disabled, row, showCodeColumn, store, worker }) => {
        const { rule } = row;
        const draggable = !disabled && canReorder;
        const expanded = store.expandedRuleId === rule.id;
        const isDragging = store.draggingRuleId === rule.id;
        const isDragOver =
            store.dragOverRuleId === rule.id && Boolean(store.draggingRuleId) && store.draggingRuleId !== rule.id;
        const actionMeta = getActionMeta(rule.action.type);
        const availableScopes = store.getAvailableMatchScopesForWorker();
        const ruleNameError = store.getRuleNameError(rule.id, rule.name);

        return (
            <S.RuleCard dragging={isDragging} dragOver={isDragOver}>
                <S.RuleHeader
                    type="button"
                    columns={columns}
                    data-allow-interaction="true"
                    aria-expanded={expanded}
                    onClick={() => store.toggleRuleExpanded(rule.id)}
                    draggable={draggable}
                    onDragStart={event => {
                        if (!draggable) {
                            return;
                        }
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('text/plain', rule.id);
                        store.setDraggingRule(rule.id);
                    }}
                    onDragOver={event => {
                        if (!store.draggingRuleId) {
                            return;
                        }
                        event.preventDefault();
                        store.setDragOverRule(rule.id);
                    }}
                    onDrop={event => {
                        event.preventDefault();
                        store.reorderRule(rule.id);
                    }}
                    onDragEnd={() => store.setDraggingRule(null)}>
                    <span>
                        {!disabled && (
                            <S.DragHandle
                                locked={!canReorder}
                                title={canReorder ? undefined : 'Reorder disabled while filtered or grouped'}
                                onClick={event => event.stopPropagation()}>
                                <Icon path={mdiDragVertical} size={0.85} aria-hidden={true} />
                            </S.DragHandle>
                        )}
                    </span>
                    <S.PriorityBadge>{row.priority}</S.PriorityBadge>
                    <S.RuleName title={rule.name}>{rule.name}</S.RuleName>
                    {showCodeColumn && (
                        <S.RuleChips>
                            {row.codeChips.length ? (
                                row.codeChips.map((chip, chipIndex) => (
                                    <S.RuleCodeChip key={`${chip.label}-${chipIndex}`} kind={chip.kind}>
                                        {chip.label}
                                    </S.RuleCodeChip>
                                ))
                            ) : (
                                <S.RuleCellEmpty>—</S.RuleCellEmpty>
                            )}
                        </S.RuleChips>
                    )}
                    <S.RuleCell title={row.otherConditions.join('  AND  ') || undefined}>
                        {row.otherConditions.length ? (
                            row.otherConditions.join(' · ')
                        ) : (
                            <S.RuleCellEmpty>—</S.RuleCellEmpty>
                        )}
                    </S.RuleCell>
                    <S.ActionBadge variant={getActionBadgeVariant(rule.action.type)}>
                        <Icon path={getActionIcon(rule.action.type)} size={0.65} aria-hidden={true} />
                        {actionMeta.label}
                    </S.ActionBadge>
                    <Icon
                        path={expanded ? mdiChevronUp : mdiChevronDown}
                        size={0.9}
                        color="#8e9293"
                        aria-hidden={true}
                    />
                </S.RuleHeader>

                {expanded && (
                    <S.RuleBody data-readonly={disabled ? 'true' : undefined}>
                        <div style={{ marginBottom: 18 }}>
                            <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.ruleName}>
                                Rule Name (This is the Task Category)
                            </ConfigurationFieldLabel>
                            <S.FieldInput
                                value={rule.name}
                                disabled={disabled}
                                readOnly={disabled}
                                maxLength={RULE_NAME_MAX_LENGTH}
                                onChange={event => {
                                    if (!disabled) {
                                        store.renameRule(rule.id, event.target.value);
                                    }
                                }}
                            />
                            <ConfigurationFieldError message={ruleNameError} />
                        </div>

                        <S.SectionLabel>
                            <S.WhenTag>WHEN</S.WhenTag>
                            <S.SectionHint>all match blocks must match (AND)</S.SectionHint>
                        </S.SectionLabel>

                        <S.MatchBlockStack>
                            {rule.match.map((match, matchIndex) => (
                                <ConfigurationRuleMatchBlock
                                    key={`${rule.id}-${matchIndex}-${match.scope}`}
                                    rule={rule}
                                    match={match}
                                    matchIndex={matchIndex}
                                    removable={!disabled && rule.match.length > 1}
                                    availableScopes={availableScopes}
                                    store={store}
                                    disabled={disabled}
                                />
                            ))}
                        </S.MatchBlockStack>

                        {!disabled && (
                            <S.AddMatchButton
                                type="button"
                                disabled={disabled}
                                onClick={() => store.addMatchBlock(rule.id)}>
                                <Icon path={mdiPlus} size={0.75} aria-hidden={true} />
                                Add condition (AND)
                            </S.AddMatchButton>
                        )}

                        <S.SectionLabel>
                            <S.ThenTag>THEN</S.ThenTag>
                            <S.SectionHint>route to action bucket</S.SectionHint>
                        </S.SectionLabel>

                        <ConfigurationRuleActionPanel rule={rule} store={store} workerId={worker.id} disabled={disabled} />

                        {!disabled && (
                            <S.RuleFooter>
                                <S.FooterButton
                                    type="button"
                                    disabled={disabled || !canReorder}
                                    title={canReorder ? undefined : 'Reorder disabled while filtered or grouped'}
                                    onClick={() => store.moveRule(rule.id, -1)}>
                                    <Icon path={mdiArrowUp} size={0.65} aria-hidden={true} />
                                    Up
                                </S.FooterButton>
                                <S.FooterButton
                                    type="button"
                                    disabled={disabled || !canReorder}
                                    title={canReorder ? undefined : 'Reorder disabled while filtered or grouped'}
                                    onClick={() => store.moveRule(rule.id, 1)}>
                                    <Icon path={mdiArrowDown} size={0.65} aria-hidden={true} />
                                    Down
                                </S.FooterButton>
                                <S.FooterSpacer />
                                <S.FooterButton
                                    type="button"
                                    danger
                                    disabled={disabled}
                                    onClick={() => store.deleteRule(rule.id)}>
                                    <Icon path={mdiDelete} size={0.65} aria-hidden={true} />
                                    Delete rule
                                </S.FooterButton>
                            </S.RuleFooter>
                        )}
                    </S.RuleBody>
                )}
            </S.RuleCard>
        );
    },
);
